import React, { useCallback, useEffect, useState } from 'react';
import { useEditor, useNode, type Node } from '@craftjs/core';
import { DimensionsFields, type DimensionDragOptions } from './DimensionsFields';
import {
  emptyContainer,
  getAdjacentFillableSibling,
  getAllowedResizeControls,
  getBoundaryDomElement,
  hasFixedHeightV2,
  isRelativelyFillableElementV2,
} from '@/utils/helperFunc';
import { useAppSelector } from '@/store/hooks';
import { selectScale } from '@/store/zoom/selectors';
import { selectBreakpoint } from '@/store/system/selectors';
import { useContainerHelper } from '@/hooks/useContainerHelper';
import { useCraftHelper } from '@/hooks/useCraftHelper';
import {
  getEffectiveMaxWidthPx,
  getElementDimensionsWithPadding,
  getElementDimensionsWithoutPadding,
} from '@/utils/numToMeasurement';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import type { ContainerProps } from '@builder4/shared/components/Container';
import {
  CONTENT_BLOCK_NAME,
  FORM_GAP,
  IMAGE_NODE_NAME,
  FORM_NODE_NAME,
  VIDEO_NODE_NAME,
  ICON_NODE_NAME,
  EMBED_NODE_NAME,
} from '@builder4/shared/utils/constants';

interface NodeDimensionSettingsProps {
  className?: string;
}

export const NodeDimensionSettings: React.FC<NodeDimensionSettingsProps> = ({ className = '' }) => {
  const scale = useAppSelector(selectScale);
  const breakpoint = useAppSelector(selectBreakpoint);
  const { getFillableChilds, getTallestChild } = useContainerHelper();
  const { getNodeContentDimensions } = useCraftHelper();
  const {
    actions: { setProp },
    id,
    parent,
    props,
    node,
    displayName,
    nodes: childIds,
  } = useNode((node) => ({
    props: node.data.props,
    parent: node.data.parent,
    node: node,
    id: node.id,
    displayName: node.data.displayName,
    nodes: node.data?.nodes ?? [],
  }));

  const { getBPProp, setBPProp } = useBreakpointAwareProp(displayName);
  const aspectRatioLock = getBPProp(props, 'aspectRatioLock');
  const aspectRatio = getBPProp(props, 'aspectRatio');
  const widthProp = getBPProp(props, 'width');
  const heightProp = getBPProp(props, 'height');
  const rightProp = getBPProp(props, 'right');
  const bottomProp = getBPProp(props, 'bottom');
  const maxWidthProp = getBPProp(props, 'maxWidth');
  const disableMaxWidth = getBPProp(props, 'disableMaxWidth');
  const isAspectRatioLocked = aspectRatioLock && aspectRatio && aspectRatio > 0;
  const position = getBPProp(props, 'position');
  const isAbsolutelyPositioned = position === 'absolute' || position === 'fixed';

  const { parentDirection, parentNode, isActive, actions, query } = useEditor((_, query) => {
    const resolvedParentNode = parent ? query.node(parent as string)?.get() : undefined;
    const isActive = !id ? false : query.getEvent('selected').contains(id);
    if (!resolvedParentNode)
      return {
        parentDirection: undefined,
        parentNode: undefined,
        isActive,
        query,
      };

    const direction = getBPProp(resolvedParentNode.data.props, 'flexDirection');

    return {
      parentDirection: direction,
      parentNode: resolvedParentNode,
      isActive,
      query,
    };
  });
  const allowedControls = getAllowedResizeControls({
    node,
    isActive,
    parentDirection,
    parentNode,
    id: id,
  });

  // Helper function to calculate aspect ratio from DOM dimensions
  const calculateAspectRatioFromDOM = (): number | null => {
    if (!node?.dom) return null;
    const nodeDOM = getBoundaryDomElement(node.dom as HTMLElement);
    const width = nodeDOM.offsetWidth;
    const height = nodeDOM.offsetHeight;
    if (width <= 0 || height <= 0) return null;
    return width / height;
  };

  // Read dimensions from DOM after updates using requestAnimationFrame
  const [dimensionsInPx, setDimensionsInPx] = useState({ width: 0, height: 0 });
  // Max bounds in px
  const [maxWidthPx, setMaxWidthPx] = useState<number | null>(null);
  const [maxHeightPx, setMaxHeightPx] = useState<number | null>(null);
  const [minHeightPx, setMinHeightPx] = useState<number | null>(null);

  // Max height: clear when not applicable (mirrors Resizer; value is set on height drag start)
  useEffect(() => {
    if (
      isAbsolutelyPositioned ||
      !parentNode ||
      !hasFixedHeightV2(getBPProp(parentNode.data.props, 'height'))
    ) {
      setMaxHeightPx(null);
    }
  }, [isAbsolutelyPositioned, parentNode]);

  // Min height: clear when not applicable (mirrors Resizer; value is set on height drag start)
  useEffect(() => {
    if (isAbsolutelyPositioned || !node) {
      setMinHeightPx(null);
    }
  }, [isAbsolutelyPositioned, node]);

  // Compute and set max height on height drag start (fresh DOM), like Resizer's setMaxHeight in handleResizeStart
  const computeAndSetMaxHeightPx = useCallback(() => {
    if (
      isAbsolutelyPositioned ||
      !parentNode ||
      !hasFixedHeightV2(getBPProp(parentNode.data.props, 'height'))
    ) {
      setMaxHeightPx(null);
      return;
    }
    const parentContentHeight = getNodeContentDimensions(parentNode as Node).height;
    setMaxHeightPx(Math.round(parentContentHeight));
  }, [isAbsolutelyPositioned, parentNode, getBPProp, getNodeContentDimensions]);

  // Compute and set min height on height drag start (fresh DOM), like Resizer's setContainerMinHeight / setFormMinHeight
  const computeAndSetMinHeightPx = useCallback(() => {
    if (isAbsolutelyPositioned || !node) {
      setMinHeightPx(null);
      return;
    }
    const padding = getBPProp(props, 'padding') ?? [0, 0, 0, 0];
    const borderWidth = getBPProp(props, 'borderWidth') || 0;

    if (displayName === CONTENT_BLOCK_NAME && !emptyContainer(node)) {
      const flexDirection = getBPProp(props, 'flexDirection');
      const gap = getBPProp(props, 'gap') || 0;
      if (flexDirection === 'column') {
        let totalHeight = padding[0] + padding[2] + borderWidth * 2;
        childIds.forEach((childId: string) => {
          const childNode = query.node(childId)?.get();
          if (!childNode?.dom) return;
          const childPosition = getBPProp(childNode.data.props, 'position');
          if (childPosition === 'relative' || childPosition === 'sticky') {
            totalHeight += (childNode.dom as HTMLElement).getBoundingClientRect().height / scale;
            totalHeight += gap;
          }
        });
        if (childIds.length > 0) totalHeight -= gap;
        setMinHeightPx(Math.round(totalHeight));
      } else {
        const tallestChild = getTallestChild(id);
        if (tallestChild?.dom) {
          const childHeight =
            (tallestChild.dom as HTMLElement).getBoundingClientRect().height / scale;
          setMinHeightPx(Math.round(childHeight + padding[0] + padding[2] + borderWidth * 2));
        } else {
          setMinHeightPx(null);
        }
      }
    } else if (displayName === FORM_NODE_NAME) {
      let totalHeight = padding[0] + padding[2] + borderWidth * 2;
      childIds.forEach((childId: string) => {
        const childNode = query.node(childId)?.get();
        if (childNode?.dom) {
          totalHeight += (childNode.dom as HTMLElement).getBoundingClientRect().height / scale;
          totalHeight += FORM_GAP;
        }
      });
      if (childIds.length > 0) totalHeight -= FORM_GAP;
      setMinHeightPx(Math.round(totalHeight));
    } else {
      setMinHeightPx(null);
    }
  }, [
    isAbsolutelyPositioned,
    node,
    displayName,
    props,
    childIds,
    id,
    scale,
    query,
    getTallestChild,
    getBPProp,
  ]);

  // Compute and set max width on width drag start (fresh DOM), like Resizer's updateMaxWidthInPx on handleResizeStart
  const computeAndSetMaxWidthPx = useCallback(() => {
    if (!parentNode?.dom) {
      setMaxWidthPx(null);
      return;
    }
    if (displayName === CONTENT_BLOCK_NAME && disableMaxWidth) {
      setMaxWidthPx(null);
      return;
    }
    const parentElement = getBoundaryDomElement(parentNode.dom as HTMLElement);
    const parentWidth = getElementDimensionsWithoutPadding(parentElement).width;

    let rowMaxPx: number | null = null;
    if (parentDirection === 'row' && node && isRelativelyFillableElementV2(node, breakpoint)) {
      const fillableSiblings = getFillableChilds(parentNode.id as string);
      if (fillableSiblings.length >= 2 && node.dom) {
        const parentGap = getBPProp(parentNode.data.props, 'gap') || 0;
        const currentWidthPx = getElementDimensionsWithPadding(node.dom as HTMLElement).width;
        const minWidthPx = getBPProp(props, 'minWidth');
        const minSiblingPx =
          minWidthPx != null && Number.isFinite(minWidthPx) ? minWidthPx : 0.05 * parentWidth;
        let cap = currentWidthPx;
        const leftSibling = getAdjacentFillableSibling(id, fillableSiblings, 'left');
        const rightSibling = getAdjacentFillableSibling(id, fillableSiblings, 'right');
        if (leftSibling?.dom) {
          const leftWidthPx = getElementDimensionsWithPadding(leftSibling.dom as HTMLElement).width;
          cap += Math.max(0, leftWidthPx - minSiblingPx);
        }
        if (rightSibling?.dom) {
          const rightWidthPx = getElementDimensionsWithPadding(
            rightSibling.dom as HTMLElement,
          ).width;
          cap += Math.max(0, rightWidthPx - minSiblingPx);
        }
        rowMaxPx = Math.max(0, cap - parentGap * 2);
      }
    }

    const effectiveMaxPx = getEffectiveMaxWidthPx(parentWidth, maxWidthProp, rowMaxPx);
    setMaxWidthPx(effectiveMaxPx);
  }, [
    parentNode,
    props,
    displayName,
    disableMaxWidth,
    parentDirection,
    node,
    breakpoint,
    id,
    getFillableChilds,
    maxWidthProp,
  ]);

  useEffect(() => {
    if (!node?.dom) {
      setDimensionsInPx({ width: 0, height: 0 });
      return;
    }

    // Use requestAnimationFrame to read DOM values after browser paint
    const rafId = requestAnimationFrame(() => {
      const nodeDOM = getBoundaryDomElement(node.dom as HTMLElement);
      const widthInPx = nodeDOM.getBoundingClientRect().width / scale;
      const heightInPx = nodeDOM.getBoundingClientRect().height / scale;
      setDimensionsInPx({
        width: Math.ceil(widthInPx),
        height: Math.round(heightInPx),
      });
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [node?.dom, scale, widthProp, heightProp, rightProp, bottomProp]);

  const updateHeight = (value: number, skipAspectRatioEnforcement = false) => {
    const actionMethod = actions.history.throttle(1000).setProp;
    const parentDOM = parentNode?.dom as HTMLElement;
    if (!parentDOM) return;

    // Check which anchors are enabled
    const positionAnchorsState = getBPProp(props, 'positionAnchorsState');
    const isTopEnabled = positionAnchorsState?.enableTop ?? false;
    const isBottomEnabled = positionAnchorsState?.enableBottom ?? false;

    const parentHeight = getBoundaryDomElement(parentDOM).getBoundingClientRect().height / scale;
    const isAbsolutelyPositioned = position === 'absolute' || position === 'fixed';

    // If any vertical anchor is enabled, update the appropriate anchor instead of height
    if (isAbsolutelyPositioned) {
      let anchorToUpdate: 'top' | 'bottom' | null = null;
      let newAnchorValue: string | null = null;
      if (isBottomEnabled && !isTopEnabled) {
        const currentBottom = getBPProp(props, 'bottom');
        const bottomPx = currentBottom ? parseFloat(currentBottom.replace('px', '')) || 0 : 0;
        const newTopPx = parentHeight - bottomPx - value;
        anchorToUpdate = 'top';
        newAnchorValue = `${Math.round(newTopPx)}px`;
      } else {
        const currentTop = getBPProp(props, 'top');
        const topPx = currentTop ? parseFloat(currentTop.replace('px', '')) || 0 : 0;
        const newBottomPx = parentHeight - topPx - value;
        anchorToUpdate = 'bottom';
        newAnchorValue = `${Math.round(newBottomPx)}px`;
      }

      if (anchorToUpdate && newAnchorValue) {
        actionMethod(id, (props: ContainerProps) => {
          setBPProp(props, anchorToUpdate, newAnchorValue);
          if (!(isTopEnabled && isBottomEnabled)) {
            setBPProp(props, 'height', `${value}px`);
          }
        });

        if (!skipAspectRatioEnforcement && isAspectRatioLocked) {
          const newWidth = Math.round(value * aspectRatio);
          updateWidth(newWidth, true); // Pass true to prevent infinite loop
        }
        return;
      }
    }

    actionMethod(id, (props: ContainerProps) => {
      setBPProp(props, 'height', `${value}px`);
    });

    // If aspect ratio is locked, also update width to maintain ratio
    if (!skipAspectRatioEnforcement && isAspectRatioLocked) {
      const newWidth = Math.round(value * aspectRatio);
      updateWidth(newWidth, true); // Pass true to prevent infinite loop
    }
  };

  const updateWidth = (value: number, skipAspectRatioEnforcement = false) => {
    const actionMethod = actions.history.throttle(1000).setProp;
    const parentDOM = parentNode?.dom as HTMLElement;
    if (!parentDOM) return;

    // Check which anchors are enabled
    const positionAnchorsState = getBPProp(props, 'positionAnchorsState');
    const isLeftEnabled = positionAnchorsState?.enableLeft ?? false;
    const isRightEnabled = positionAnchorsState?.enableRight ?? false;

    // let parentWidth = getBoundaryDomElement(parentDOM).getBoundingClientRect().width / scale
    const parentElement = getBoundaryDomElement(parentDOM as HTMLElement);
    const parentWidth = getElementDimensionsWithoutPadding(parentElement).width;

    const currentWidth = getBPProp(props, 'width');
    const isCurrentlyPercentage = currentWidth.endsWith('%') ?? false;
    const currentWidthPx = dimensionsInPx.width;
    const widthDifference = value - currentWidthPx;
    const onePixelPercentage = (1 / parentWidth) * 100;
    const inc = widthDifference * onePixelPercentage;

    // Calculate new width for current element
    const newWidth = isCurrentlyPercentage ? `${parseFloat(currentWidth) + inc}%` : `${value}px`;

    // If any horizontal anchor is enabled, update the appropriate anchor instead of width
    if (isAbsolutelyPositioned) {
      let anchorToUpdate: 'left' | 'right' | null = null;
      let newAnchorValue: string | null = null;

      if (isRightEnabled && !isLeftEnabled) {
        const currentRight = getBPProp(props, 'right');
        const rightPx = currentRight ? parseFloat(currentRight.replace('px', '')) || 0 : 0;
        const newLeftPx = parentWidth - rightPx - value;
        anchorToUpdate = 'left';
        newAnchorValue = `${Math.round(newLeftPx)}px`;
      } else {
        const currentLeft = getBPProp(props, 'left');
        const leftPx = currentLeft ? parseFloat(currentLeft.replace('px', '')) || 0 : 0;
        const newRightPx = parentWidth - leftPx - value;
        anchorToUpdate = 'right';
        newAnchorValue = `${Math.round(newRightPx)}px`;
      }

      if (anchorToUpdate && newAnchorValue) {
        actionMethod(id, (props: ContainerProps) => {
          setBPProp(props, anchorToUpdate, newAnchorValue);
          if (!(isLeftEnabled && isRightEnabled)) {
            setBPProp(props, 'width', newWidth);
          }
        });

        // If aspect ratio is locked, also update height to maintain ratio
        if (!skipAspectRatioEnforcement && isAspectRatioLocked) {
          const newHeight = Math.round(value / aspectRatio);
          updateHeight(newHeight, true); // Pass true to prevent infinite loop
        }
        return;
      }
    }

    actionMethod(id, (props: ContainerProps) => {
      setBPProp(props, 'width', newWidth);
      setBPProp(props, 'flexBasis', newWidth);
    });

    // If aspect ratio is locked, also update height to maintain ratio
    if (!skipAspectRatioEnforcement && aspectRatioLock && aspectRatio && aspectRatio > 0) {
      const newHeight = Math.round(value / aspectRatio);
      updateHeight(newHeight, true); // Pass true to prevent infinite loop
    }
  };

  /**
   * Wrapper that applies max-height checks (and, when aspect ratio is locked, max-width checks)
   * before delegating to updateHeight.
   */
  const applyHeightWithConstraints = (nextHeight: number) => {
    let targetHeight = nextHeight;

    // Clamp to minHeightPx if defined (Container/Form content-based min)
    if (minHeightPx != null && nextHeight < minHeightPx) {
      targetHeight = minHeightPx;
    }

    // Clamp to maxHeightPx if defined
    if (maxHeightPx != null && targetHeight > maxHeightPx) {
      targetHeight = maxHeightPx;
    }

    // When aspect ratio is locked, ensure the corresponding width does not exceed its max
    if (isAspectRatioLocked && aspectRatio) {
      const correspondingWidth = Math.round(targetHeight * aspectRatio);

      if (maxWidthPx != null && correspondingWidth > maxWidthPx) {
        // Do not allow the height change if it would push width beyond its max
        return;
      }
    }

    updateHeight(targetHeight);
  };

  /**
   * Wrapper that applies max-width checks (and, when aspect ratio is locked, max-height checks)
   * before delegating to updateWidth.
   */
  const applyWidthWithConstraints = (nextWidth: number) => {
    let targetWidth = nextWidth;

    // Clamp to maxWidthPx if defined
    if (maxWidthPx != null && nextWidth > maxWidthPx) {
      targetWidth = maxWidthPx;
    }

    // When aspect ratio is locked, ensure the corresponding height does not exceed its max
    if (isAspectRatioLocked && aspectRatio) {
      const correspondingHeight = Math.round(targetWidth / aspectRatio);

      if (maxHeightPx != null && correspondingHeight > maxHeightPx) {
        // Do not allow the width change if it would push height beyond its max
        return;
      }
    }

    updateWidth(targetWidth);
  };

  const onToggleAspectRatioLock = () => {
    setProp((props: ContainerProps) => {
      const currentLock = getBPProp(props, 'aspectRatioLock');
      const newLockState = !currentLock;

      // When locking (transitioning from false → true), calculate and store ratio
      if (newLockState) {
        const calculatedRatio = calculateAspectRatioFromDOM();
        if (calculatedRatio !== null) {
          setBPProp(props, 'aspectRatio', calculatedRatio);
        }
      } else {
        setBPProp(props, 'aspectRatio', undefined);
        setBPProp(props, 'height', `${dimensionsInPx.height}px`);
      }

      setBPProp(props, 'aspectRatioLock', newLockState);
    });
  };

  const handleDragStart = useCallback(
    (_value: number, options: DimensionDragOptions) => {
      if (options.isWidth || isAspectRatioLocked) computeAndSetMaxWidthPx();
      if (options.isHeight) {
        computeAndSetMaxHeightPx();
        computeAndSetMinHeightPx();
      }
    },
    [
      computeAndSetMaxWidthPx,
      computeAndSetMaxHeightPx,
      computeAndSetMinHeightPx,
      isAspectRatioLocked,
    ],
  );

  const handleChange = (value: number, options: DimensionDragOptions) => {
    if (options.isHeight) {
      applyHeightWithConstraints(value);
    } else {
      applyWidthWithConstraints(value);
    }
  };

  const handleChangeComplete = (
    value: number,
    _initialValue: number,
    options: DimensionDragOptions,
  ) => {
    if (Number.isFinite(value)) {
      if (options.isHeight) {
        applyHeightWithConstraints(value);
      } else {
        applyWidthWithConstraints(value);
      }
    }
  };

  const hasAspectControl = (displayName: string) => {
    const allowedNodes = [
      CONTENT_BLOCK_NAME,
      IMAGE_NODE_NAME,
      FORM_NODE_NAME,
      VIDEO_NODE_NAME,
      ICON_NODE_NAME,
      EMBED_NODE_NAME,
    ];
    return allowedNodes.includes(displayName);
  };

  return (
    <DimensionsFields
      className={className}
      hasAspectControl={hasAspectControl(displayName)}
      aspectRatioLock={aspectRatioLock}
      onChangeAspectRatioLock={onToggleAspectRatioLock}
      disableHeight={!(allowedControls.bottom || allowedControls.top)}
      disableWidth={!(allowedControls.left || allowedControls.right)}
      width={dimensionsInPx.width}
      height={dimensionsInPx.height}
      onChange={handleChange}
      onChangeComplete={handleChangeComplete}
      onDragStart={handleDragStart}
      onDrag={(value, options) =>
        options.isHeight ? applyHeightWithConstraints(value) : applyWidthWithConstraints(value)
      }
      onDragEnd={(value, options) =>
        options.isHeight ? applyHeightWithConstraints(value) : applyWidthWithConstraints(value)
      }
    />
  );
};
